/**
 * Auth Store
 *
 * Store Zustand untuk menyimpan state autentikasi dan data user aktif.
 *
 * PENTING: Nama variabel user aktif disamakan dengan Booku Lama (mdl_VariabelUmum.vb:117-151)
 * untuk memudahkan komparasi dan konsistensi.
 *
 * KEAMANAN: State autentikasi TIDAK di-persist ke localStorage.
 * Saat aplikasi ditutup, user otomatis logout dan harus login ulang.
 * Ini untuk keamanan data akuntansi yang sensitif.
 *
 * Penggunaan:
 *   const { UserAktif, LevelUserAktif, ClusterFinance } = useAuthStore();
 */

import { create } from 'zustand';
import {
  LevelUser_09_SuperUser,
  LevelUser_03_Direktur,
  LevelUser_81_TimIT,
  LevelUser_99_AppDeveloper,
} from '@/modules/data/user/types';
import { useAppTabsStore } from '@/stores/appTabsStore';
import { useSubMenuStore } from '@/stores/subMenuStore';

// ============================================
// INTERFACES
// ============================================

interface User {
  id: number;
  username: string;
  fullName: string;
  email?: string;
  level: number;
  jabatan: string;
  clusterFinance: boolean;
  clusterAccounting: boolean;
}

interface Tenant {
  code: string;
  name: string;
  // Company Profile (startup variables - dimuat saat login)
  npwp?: string;
  alamat?: string;
  sebagaiPerusahaanDagang: boolean;
  sebagaiPerusahaanManufaktur: boolean;
  sebagaiPerusahaanJasa: boolean;
}

interface AuthState {
  // Internal state
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;

  // ============================================
  // VARIABEL PUBLIC USER AKTIF
  // Nama variabel disamakan dengan Booku Lama
  // (mdl_VariabelUmum.vb:117-151)
  // ============================================

  /** Username user yang sedang login (mdl_VariabelUmum.vb:117) */
  UserAktif: string;

  /** Nama lengkap user yang sedang login (mdl_VariabelUmum.vb:118) */
  NamaUserAktif: string;

  /** Level user yang sedang login (mdl_VariabelUmum.vb:119) */
  LevelUserAktif: number;

  /** Jabatan user yang sedang login (mdl_VariabelUmum.vb:120) */
  JabatanUserAktif: string;

  /** Akses cluster Finance (mdl_VariabelUmum.vb:148) */
  ClusterFinance: number;

  /** Akses cluster Accounting (mdl_VariabelUmum.vb:149) */
  ClusterAccounting: number;

  /** Status aktif user (mdl_VariabelUmum.vb:151) */
  StatusAktifUser: number;

  // ============================================
  // VARIABEL PUBLIC COMPANY PROFILE
  // Startup variables - dimuat saat login, konstant selama sesi
  // (mdl_VariabelUmum.vb:36-38)
  // ============================================

  /** Nama Perusahaan */
  NamaPerusahaan: string;

  /** NPWP Perusahaan (format: XX.XXX.XXX.X-XXX.XXX) */
  NPWPPerusahaan: string;

  /** Alamat Perusahaan */
  AlamatPerusahaan: string;

  /** Jenis Usaha: Perusahaan Dagang (mdl_VariabelUmum.vb:36) */
  sebagaiPerusahaanDagang: boolean;

  /** Jenis Usaha: Perusahaan Manufaktur (mdl_VariabelUmum.vb:37) */
  sebagaiPerusahaanManufaktur: boolean;

  /** Jenis Usaha: Perusahaan Jasa (mdl_VariabelUmum.vb:38) */
  sebagaiPerusahaanJasa: boolean;

  // ============================================
  // ACTIONS
  // ============================================

  setUser: (user: User | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  login: (user: User, tenant: Tenant) => void;
  logout: () => void;

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  /** Cek apakah user adalah SuperUser atau lebih tinggi */
  isSuperUserOrAbove: () => boolean;

  /** Cek apakah user adalah Direktur atau lebih tinggi */
  isDirekturOrAbove: () => boolean;

  /** Cek apakah user adalah Tim IT atau lebih tinggi */
  isTimITOrAbove: () => boolean;

  /** Cek apakah user adalah App Developer */
  isAppDeveloper: () => boolean;

  /** Cek apakah user punya akses Finance */
  hasFinanceAccess: () => boolean;

  /** Cek apakah user punya akses Accounting */
  hasAccountingAccess: () => boolean;
}

// ============================================
// HELPER: Compute user aktif variables
// ============================================

interface UserAktifVariables {
  UserAktif: string;
  NamaUserAktif: string;
  LevelUserAktif: number;
  JabatanUserAktif: string;
  ClusterFinance: number;
  ClusterAccounting: number;
  StatusAktifUser: number;
}

function computeUserAktifVariables(user: User | null): UserAktifVariables {
  if (!user) {
    return {
      UserAktif: '',
      NamaUserAktif: '',
      LevelUserAktif: 0,
      JabatanUserAktif: '',
      ClusterFinance: 0,
      ClusterAccounting: 0,
      StatusAktifUser: 0,
    };
  }

  return {
    UserAktif: user.username,
    NamaUserAktif: user.fullName,
    LevelUserAktif: user.level,
    JabatanUserAktif: user.jabatan,
    ClusterFinance: user.clusterFinance ? 1 : 0,
    ClusterAccounting: user.clusterAccounting ? 1 : 0,
    StatusAktifUser: 1, // Jika login berhasil, berarti aktif
  };
}

// ============================================
// HELPER: Compute company profile variables
// ============================================

interface CompanyProfileVariables {
  NamaPerusahaan: string;
  NPWPPerusahaan: string;
  AlamatPerusahaan: string;
  sebagaiPerusahaanDagang: boolean;
  sebagaiPerusahaanManufaktur: boolean;
  sebagaiPerusahaanJasa: boolean;
}

function computeCompanyProfileVariables(tenant: Tenant | null): CompanyProfileVariables {
  if (!tenant) {
    return {
      NamaPerusahaan: '',
      NPWPPerusahaan: '',
      AlamatPerusahaan: '',
      sebagaiPerusahaanDagang: false,
      sebagaiPerusahaanManufaktur: false,
      sebagaiPerusahaanJasa: false,
    };
  }

  return {
    NamaPerusahaan: tenant.name,
    NPWPPerusahaan: tenant.npwp || '',
    AlamatPerusahaan: tenant.alamat || '',
    sebagaiPerusahaanDagang: tenant.sebagaiPerusahaanDagang,
    sebagaiPerusahaanManufaktur: tenant.sebagaiPerusahaanManufaktur,
    sebagaiPerusahaanJasa: tenant.sebagaiPerusahaanJasa,
  };
}

// ============================================
// STORE
// ============================================

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  tenant: null,
  isAuthenticated: false,

  // Default values for user aktif variables
  ...computeUserAktifVariables(null),
  // Default values for company profile variables
  ...computeCompanyProfileVariables(null),

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
      ...computeUserAktifVariables(user),
    }),

  setTenant: (tenant) =>
    set({
      tenant,
      ...computeCompanyProfileVariables(tenant),
    }),

  login: (user, tenant) =>
    set({
      user,
      tenant,
      isAuthenticated: true,
      ...computeUserAktifVariables(user),
      ...computeCompanyProfileVariables(tenant),
    }),

  logout: () => {
    // Hapus semua data auth dari localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('auth-storage'); // Cleanup cache lama (jika ada)

    // Reset app tabs store (tutup semua tab)
    useAppTabsStore.getState().reset();

    // Reset sub menu store (tutup SubMenuBar)
    useSubMenuStore.getState().reset();

    set({
      user: null,
      tenant: null,
      isAuthenticated: false,
      ...computeUserAktifVariables(null),
      ...computeCompanyProfileVariables(null),
    });
  },

  // Helper functions
  isSuperUserOrAbove: () => get().LevelUserAktif >= LevelUser_09_SuperUser,
  isDirekturOrAbove: () => get().LevelUserAktif >= LevelUser_03_Direktur,
  isTimITOrAbove: () => get().LevelUserAktif >= LevelUser_81_TimIT,
  isAppDeveloper: () => get().LevelUserAktif === LevelUser_99_AppDeveloper,
  hasFinanceAccess: () => get().ClusterFinance === 1,
  hasAccountingAccess: () => get().ClusterAccounting === 1,
}));

// ============================================
// CLEANUP FUNCTION
// ============================================

/**
 * Membersihkan data autentikasi lama dari localStorage.
 * Dipanggil saat aplikasi startup untuk memastikan user harus login ulang.
 */
export function clearAuthDataOnStartup(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('auth-storage'); // Cache lama dari persist middleware
}

// ============================================
// HELPER HOOKS untuk akses mudah
// ============================================

/**
 * Hook untuk mendapatkan username user aktif
 */
export function useUserAktif(): string {
  return useAuthStore((state) => state.UserAktif);
}

/**
 * Hook untuk mendapatkan level user aktif
 */
export function useLevelUserAktif(): number {
  return useAuthStore((state) => state.LevelUserAktif);
}

/**
 * Hook untuk cek apakah user adalah SuperUser atau lebih tinggi
 */
export function useIsSuperUserOrAbove(): boolean {
  return useAuthStore((state) => state.isSuperUserOrAbove());
}

/**
 * Hook untuk cek akses Finance
 */
export function useHasFinanceAccess(): boolean {
  return useAuthStore((state) => state.hasFinanceAccess());
}

/**
 * Hook untuk cek akses Accounting
 */
export function useHasAccountingAccess(): boolean {
  return useAuthStore((state) => state.hasAccountingAccess());
}
